import { supabase } from '@/lib/supabase/client';
import type { TemplateCode, ModuleName, PriorityLevel } from './templateCodes';

/**
 * Options for queueing an email notification
 */
interface QueueNotificationOptions {
  templateCode: TemplateCode;
  templateData: Record<string, unknown>;
  recipientEmail: string;
  ccEmails?: string[];
  module: ModuleName;
  refTable?: string;
  refId?: string;
  priority?: PriorityLevel;
  createdBy?: string;
}

/**
 * Queue a single email notification.
 * Inserts into `email_notifications` table; the `process-email-queue` edge function picks it up.
 */
export async function queueNotification(
  options: QueueNotificationOptions
): Promise<{ success: boolean; id?: string; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('email_notifications')
      .insert({
        template_code: options.templateCode,
        template_data: options.templateData,
        recipient_email: options.recipientEmail,
        cc_emails: options.ccEmails || [],
        module: options.module,
        ref_table: options.refTable || null,
        ref_id: options.refId || null,
        priority: options.priority || 'NORMAL',
        created_by: options.createdBy || null,
      })
      .select('id')
      .single();

    if (error) {
      console.error('[NotificationService] Queue failed:', error.message);
      return { success: false, error: error.message };
    }

    return { success: true, id: data?.id };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[NotificationService] Queue exception:', msg);
    return { success: false, error: msg };
  }
}

/**
 * Queue notifications to all users with specified roles.
 * Fetches user emails from `users` table filtered by role.
 */
export async function notifyByRole(
  templateCode: TemplateCode,
  templateData: Record<string, unknown>,
  roles: string[],
  module: ModuleName,
  refTable?: string,
  refId?: string,
  priority?: PriorityLevel
): Promise<{ success: boolean; queued: number; errors: number }> {
  try {
    const { data: users, error } = await supabase
      .from('users')
      .select('id, email')
      .in('role', roles);

    if (error) {
      console.error('[NotificationService] Failed to fetch users by role:', error.message);
      return { success: false, queued: 0, errors: 1 };
    }

    if (!users || users.length === 0) {
      return { success: true, queued: 0, errors: 0 };
    }

    let queued = 0;
    let errors = 0;

    for (const user of users) {
      if (!user.email) continue;
      const result = await queueNotification({
        templateCode,
        templateData,
        recipientEmail: user.email,
        module,
        refTable,
        refId,
        priority,
        createdBy: user.id,
      });
      if (result.success) queued++;
      else errors++;
    }

    return { success: errors === 0, queued, errors };
  } catch (err) {
    console.error('[NotificationService] notifyByRole exception:', err);
    return { success: false, queued: 0, errors: 1 };
  }
}

/**
 * Queue notification to a specific user by their user ID.
 */
export async function notifyUser(
  templateCode: TemplateCode,
  templateData: Record<string, unknown>,
  userId: string,
  module: ModuleName,
  refTable?: string,
  refId?: string,
  priority?: PriorityLevel
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: user, error } = await supabase
      .from('users')
      .select('id, email')
      .eq('id', userId)
      .single();

    if (error || !user?.email) {
      return { success: false, error: 'User not found or has no email' };
    }

    return queueNotification({
      templateCode,
      templateData,
      recipientEmail: user.email,
      module,
      refTable,
      refId,
      priority,
      createdBy: userId,
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { success: false, error: msg };
  }
}

// ─── Module-specific helpers ────────────────────────────────────────────────

const OWNER_ROLES = ['super_admin', 'owner'];
const PROCUREMENT_ROLES = [...OWNER_ROLES, 'procurement_officer'];
const PRODUCTION_ROLES = [...OWNER_ROLES, 'production_manager'];
const INVENTORY_ROLES = [...OWNER_ROLES, 'inventory_manager'];
const PROJECT_ROLES = [...OWNER_ROLES, 'project_manager'];

/**
 * Notify Owner + Procurement managers
 */
export function notifyProcurement(
  templateCode: TemplateCode,
  templateData: Record<string, unknown>,
  refTable?: string,
  refId?: string,
  priority?: PriorityLevel
) {
  return notifyByRole(templateCode, templateData, PROCUREMENT_ROLES, 'procurement', refTable, refId, priority);
}

/**
 * Notify Owner + Production managers
 */
export function notifyProduction(
  templateCode: TemplateCode,
  templateData: Record<string, unknown>,
  refTable?: string,
  refId?: string,
  priority?: PriorityLevel
) {
  return notifyByRole(templateCode, templateData, PRODUCTION_ROLES, 'production', refTable, refId, priority);
}

/**
 * Notify Owner + Inventory managers
 */
export function notifyInventory(
  templateCode: TemplateCode,
  templateData: Record<string, unknown>,
  refTable?: string,
  refId?: string,
  priority?: PriorityLevel
) {
  return notifyByRole(templateCode, templateData, INVENTORY_ROLES, 'inventory', refTable, refId, priority);
}

/**
 * Notify Owner + Project managers
 */
export function notifyProjects(
  templateCode: TemplateCode,
  templateData: Record<string, unknown>,
  refTable?: string,
  refId?: string,
  priority?: PriorityLevel
) {
  return notifyByRole(templateCode, templateData, PROJECT_ROLES, 'projects', refTable, refId, priority);
}

/**
 * Notify Owner only
 */
export function notifyOwner(
  templateCode: TemplateCode,
  templateData: Record<string, unknown>,
  module: ModuleName,
  refTable?: string,
  refId?: string,
  priority?: PriorityLevel
) {
  return notifyByRole(templateCode, templateData, OWNER_ROLES, module, refTable, refId, priority);
}

/**
 * Notify multiple role groups (e.g., both Production + Inventory managers)
 */
export function notifyMultipleRoles(
  templateCode: TemplateCode,
  templateData: Record<string, unknown>,
  roles: string[],
  module: ModuleName,
  refTable?: string,
  refId?: string,
  priority?: PriorityLevel
) {
  const uniqueRoles = [...new Set([...OWNER_ROLES, ...roles])];
  return notifyByRole(templateCode, templateData, uniqueRoles, module, refTable, refId, priority);
}

/**
 * Trigger the send-email edge function to immediately process the queue.
 * Call this after queueing high-priority notifications for faster delivery.
 */
export async function triggerEmailProcessing(notificationId?: string): Promise<void> {
  try {
    const body: Record<string, unknown> = {};
    if (notificationId) body.notification_id = notificationId;

    await supabase.functions.invoke('send-email', {
      body: JSON.stringify(body),
    });
  } catch (err) {
    console.error('[NotificationService] Failed to trigger email processing:', err);
  }
}
